import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Clock, CreditCard, Pencil, Plus, Minus, Trash2, Loader2, X } from 'lucide-react';

const isEditableReservation = (order) =>
  ['zelle', 'cash_app'].includes(order?.payment_method) &&
  order?.order_status === 'reserved' &&
  !['paid', 'confirmed'].includes(order?.payment_status);

const ReservationManager = ({ order, authFetch, apiUrl, settings }) => {
  const navigate = useNavigate();
  const [showPay, setShowPay] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [selProduct, setSelProduct] = useState('');
  const [selVariant, setSelVariant] = useState('');
  const [saving, setSaving] = useState(false);

  const isZelle = order.payment_method === 'zelle';
  const payLabel = isZelle ? 'Zelle' : 'Cash App';
  const handle = isZelle ? (settings?.zelle_handle || '') : (settings?.cashapp_handle || '');
  const qrUrl = isZelle ? (settings?.zelle_qr_url || '') : (settings?.cashapp_qr_url || '');
  const note = isZelle ? (settings?.zelle_note || '') : (settings?.cashapp_note || '');

  useEffect(() => {
    if (showEdit) {
      setItems((order.items || []).map((it) => ({
        product_id: it.product_id,
        name: it.name || it.product_name || 'Item',
        vial_size: it.vial_size || it.variant_size || '',
        quantity: it.quantity || 1,
        price: it.price || 0,
        item_type: it.item_type || (String(it.product_id).startsWith('shop_') ? 'shop' : 'peptide'),
        pack_size: it.pack_size,
        color: it.color,
      })));
      if (products.length === 0) {
        authFetch(`${apiUrl}/api/products`)
          .then((r) => (r.ok ? r.json() : []))
          .then((d) => setProducts(Array.isArray(d) ? d : (d.products || [])))
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEdit]);

  const setQty = (idx, delta) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, quantity: Math.max(1, it.quantity + delta) } : it));
  };
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const addItem = () => {
    const prod = products.find((p) => p.product_id === selProduct);
    if (!prod) { toast.error('Pick a product'); return; }
    const variants = prod.variants || [];
    const v = variants.find((x) => x.size === selVariant) || variants[0];
    if (variants.length && !v) { toast.error('Pick a size'); return; }
    const price = v ? (v.price || 0) : (prod.price || 0);
    setItems((prev) => [...prev, {
      product_id: prod.product_id,
      name: prod.name,
      vial_size: v ? v.size : '',
      quantity: 1,
      price,
      item_type: 'peptide',
    }]);
    setSelProduct(''); setSelVariant('');
  };

  const estTotal = items.reduce((s, it) => s + (it.price || 0) * it.quantity, 0);

  const saveChanges = async () => {
    if (items.length === 0) { toast.error('Your order needs at least one item.'); return; }
    setSaving(true);
    try {
      const payloadItems = items.map((it) => it.item_type === 'shop'
        ? { product_id: it.product_id, quantity: it.quantity, pack_size: it.pack_size, color: it.color }
        : { product_id: it.product_id, vial_size: it.vial_size, quantity: it.quantity });
      const payload = {
        payment_method: order.payment_method,
        items: payloadItems,
        phone: order.contact?.phone || order.phone || '',
        shipping_address: order.shipping_address || {},
        contact: order.contact || {},
      };
      if (order.zelle_request_contact) payload.zelle_contact = order.zelle_request_contact;
      if (order.discount?.code) payload.discount_code = order.discount.code;
      const r = await authFetch(`${apiUrl}/api/orders/${order.order_id}/revise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Update failed');
      const data = await r.json();
      toast.success('Order updated — please pay the new amount.');
      setShowEdit(false);
      navigate(`/checkout/success?free_order=${data.order_id}`);
    } catch (e) {
      toast.error(e.message || 'Could not update order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="mx-4 mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid={`awaiting-payment-${order.order_id}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <p className="text-sm text-amber-900 flex items-center gap-2">
            <Clock className="h-4 w-4 flex-shrink-0" />
            <span><strong>Awaiting your payment.</strong> Send <strong>${(order.total || 0).toFixed(2)}</strong> via {payLabel} to complete this order.</span>
          </p>
          <div className="flex gap-2 flex-shrink-0">
            <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100"
              onClick={() => setShowPay(true)} data-testid={`view-payment-${order.order_id}`}>
              <CreditCard className="h-4 w-4 mr-1.5" /> Payment details
            </Button>
            <Button size="sm" variant="outline" className="border-slate-300"
              onClick={() => setShowEdit(true)} data-testid={`edit-order-${order.order_id}`}>
              <Pencil className="h-4 w-4 mr-1.5" /> Edit / Add items
            </Button>
          </div>
        </div>
      </div>

      {/* Payment details modal */}
      <Dialog open={showPay} onOpenChange={setShowPay}>
        <DialogContent className="max-w-md" data-testid="payment-details-modal">
          <DialogHeader><DialogTitle>Send your {payLabel} payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {qrUrl && (
              <div className="flex justify-center bg-slate-50 rounded-lg p-4">
                <img src={`${apiUrl}${qrUrl}`} alt={`${payLabel} QR`} className="w-44 h-44 object-contain rounded-lg bg-white" />
              </div>
            )}
            {handle && (
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs text-slate-500">Send to</p>
                <p className="font-mono font-semibold text-purple-700 break-all">{handle}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs text-slate-500">Exact amount</p>
                <p className="font-bold text-purple-700 text-lg">${(order.total || 0).toFixed(2)}</p>
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="text-xs text-amber-700">Include this memo</p>
                <p className="font-mono font-semibold text-amber-900 break-all">{order.order_id}</p>
              </div>
            </div>
            {note && <p className="text-xs text-slate-600">{note}</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit order modal */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-order-modal">
          <DialogHeader><DialogTitle>Edit your order</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2 mb-1">
            Saving cancels this reservation and creates an updated one. We'll email you the <strong>new total</strong> — the old amount becomes void.
          </p>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={idx} className="flex items-center justify-between gap-2 border border-slate-100 rounded-lg p-2" data-testid={`edit-line-${idx}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{it.name}</p>
                  <p className="text-xs text-slate-500">{it.vial_size} · ${(it.price || 0).toFixed(2)} ea</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => setQty(idx, -1)} className="p-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`qty-dec-${idx}`}><Minus className="h-3.5 w-3.5" /></button>
                  <span className="w-6 text-center text-sm">{it.quantity}</span>
                  <button onClick={() => setQty(idx, 1)} className="p-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`qty-inc-${idx}`}><Plus className="h-3.5 w-3.5" /></button>
                  <button onClick={() => removeItem(idx)} className="p-1 rounded text-red-500 hover:bg-red-50 ml-1" data-testid={`remove-line-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="text-sm text-red-500">Add at least one item.</p>}
          </div>

          <div className="mt-3 border-t border-slate-100 pt-3">
            <p className="text-xs font-medium text-slate-600 mb-1.5">Add an item</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <select value={selProduct} onChange={(e) => { setSelProduct(e.target.value); setSelVariant(''); }}
                className="flex-1 rounded-lg border border-slate-200 px-2 py-2 text-sm" data-testid="add-product-select">
                <option value="">Choose product…</option>
                {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
              </select>
              <select value={selVariant} onChange={(e) => setSelVariant(e.target.value)}
                className="sm:w-32 rounded-lg border border-slate-200 px-2 py-2 text-sm" data-testid="add-variant-select">
                <option value="">Size…</option>
                {(products.find((p) => p.product_id === selProduct)?.variants || []).map((v) => (
                  <option key={v.size} value={v.size}>{v.size}</option>
                ))}
              </select>
              <Button type="button" variant="outline" onClick={addItem} data-testid="add-item-btn"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
            <span className="text-sm text-slate-500">Estimated items total</span>
            <span className="font-bold text-slate-900">${estTotal.toFixed(2)}</span>
          </div>
          <p className="text-[11px] text-slate-400">Final total (incl. shipping/discounts) is calculated on the next step.</p>

          <div className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowEdit(false)}><X className="h-4 w-4 mr-1" />Cancel</Button>
            <Button className="flex-1 bg-teal-700 hover:bg-teal-800 text-white" onClick={saveChanges} disabled={saving} data-testid="save-order-changes">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save & get new total'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { isEditableReservation };
export default ReservationManager;
